import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// Every note he's left you on the homepage, kept like a little diary: grouped
// by month, newest first, each with its day and the time of day he wrote it.
// Heart the ones you love; any of those can also go into From Caleb. The pen
// in the corner opens the instructions he follows when he writes them.

// "2026-10-04" is read as that day where you are, not as UTC midnight
const dayOf = (d) => new Date(`${d}T12:00:00`);
const monthLabel = (d) => dayOf(d).toLocaleDateString([], { month: 'long', year: 'numeric' });
const dayLabel = (d) => {
  const x = dayOf(d);
  const today = new Date();
  const yest = new Date(); yest.setDate(today.getDate() - 1);
  if (x.toDateString() === today.toDateString()) return 'today';
  if (x.toDateString() === yest.toDateString()) return 'yesterday';
  return x.toLocaleDateString([], { weekday: 'long', month: 'short', day: 'numeric' });
};
const partOfDay = (iso) => {
  const h = new Date(iso).getHours();
  return h >= 22 || h < 5 ? 'late at night' : h < 12 ? 'in the morning' : h < 17 ? 'in the afternoon' : 'in the evening';
};

const Heart = ({ on, size = 17 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
    <path d="M12 20.5s-7.5-4.6-9.2-9.3C1.6 7.8 3.8 4.5 7.2 4.5c2 0 3.5 1.1 4.8 2.8 1.3-1.7 2.8-2.8 4.8-2.8 3.4 0 5.6 3.3 4.4 6.7-1.7 4.7-9.2 9.3-9.2 9.3z"
      fill={on ? '#e0788f' : 'none'} stroke={on ? '#e0788f' : '#8a7466'} strokeWidth="1.6" />
  </svg>
);

const PenIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#c9935a" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M4 20h4L19 9a2.1 2.1 0 0 0-3-3L5 17z" /><path d="M14 7l3 3" />
  </svg>
);

// the instructions he follows when he writes a note — editable, with a try-it
function PromptSheet({ token, name, onClose }) {
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState('');
  const [useChar, setUseChar] = useState(true);
  const [saving, setSaving] = useState(false);
  const [trying, setTrying] = useState(false);
  const [sample, setSample] = useState('');
  const [msg, setMsg] = useState('');

  useEffect(() => {
    axios.get(`${API}/home/note-prompt?token=${token}`)
      .then(res => { setData(res.data); setDraft(res.data.prompt); setUseChar(res.data.use_character); })
      .catch(() => setMsg("couldn't load the instructions"));
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const changed = data && (draft.trim() !== data.prompt.trim() || useChar !== data.use_character);
  const save = () => {
    setSaving(true); setMsg('');
    axios.put(`${API}/home/note-prompt`, { token, prompt: draft, use_character: useChar })
      .then(res => { setData(res.data); setDraft(res.data.prompt); setMsg('saved — his next note follows this'); })
      .catch(() => setMsg("couldn't save — try again"))
      .finally(() => setSaving(false));
  };
  const tryIt = () => {
    setTrying(true); setSample(''); setMsg('');
    axios.post(`${API}/home/note-prompt/try`, { token, prompt: draft, use_character: useChar })
      .then(res => setSample(res.data.text))
      .catch(err => setMsg(err?.response?.data?.detail || "couldn't write a sample — try again"))
      .finally(() => setTrying(false));
  };

  return (
    <div style={s.backdrop} onClick={onClose}>
      <div style={s.sheet} role="dialog" aria-modal="true" aria-label="how he writes your notes" onClick={e => e.stopPropagation()}>
        <div style={s.sheetHead}>
          <span style={s.sheetTitle}>how {name} writes your notes</span>
          <button style={s.close} onClick={onClose} aria-label="close">×</button>
        </div>
        <div style={s.sheetHint}>
          these are the instructions he follows for the homepage note. <b style={{ fontWeight: 400, color: '#c9a57a' }}>{'{time}'}</b> becomes
          “in the morning”, “late at night” and so on. your recent conversations are added after this on their own.
        </div>
        <textarea style={s.promptArea} value={draft} onChange={e => setDraft(e.target.value)} disabled={!data} aria-label="instructions" />
        <label style={s.toggleRow}>
          <input type="checkbox" checked={useChar} onChange={e => setUseChar(e.target.checked)} />
          <span>
            use his character settings too
            <span style={s.toggleSub}>his usual habits (like his emojis) can come from these. switch off and only the instructions above shape the note.</span>
          </span>
        </label>
        {sample && <div style={s.sample}><div style={s.sampleLabel}>a sample — not saved</div>{sample}</div>}
        {msg && <div style={s.sheetMsg}>{msg}</div>}
        <div style={s.sheetRow}>
          <button style={changed && !saving ? s.save : s.saveOff} disabled={!changed || saving} onClick={save}>{saving ? 'saving…' : 'save'}</button>
          <button style={s.ghost} onClick={tryIt} disabled={trying || !draft.trim()}>{trying ? 'writing…' : 'try it'}</button>
          {data && draft.trim() !== data.default.trim() && (
            <button style={s.quietBtn} onClick={() => setDraft(data.default)}>use the original</button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function CalebNotes() {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const [notes, setNotes] = useState(null);
  const [onlyKept, setOnlyKept] = useState(false);
  const [onlyWeekly, setOnlyWeekly] = useState(false);   // just his Sunday letters
  const [q, setQ] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    if (!token) { navigate('/'); return; }
    axios.get(`${API}/home/notes?token=${token}`)
      .then(res => setNotes(res.data.notes || []))
      .catch(() => { setNotes([]); setErrorMsg("couldn't load his notes — try again in a moment"); });
  }, []);

  const keep = (n) => {
    const kept = !n.kept;
    setNotes(list => list.map(x => (x.id === n.id ? { ...x, kept } : x)));
    axios.put(`${API}/home/notes/${n.id}/keep`, { token, kept })
      .catch(() => { setNotes(list => list.map(x => (x.id === n.id ? { ...x, kept: !kept } : x))); setErrorMsg("couldn't save that — try again"); });
  };
  const toKeepsakes = (n) => {
    axios.post(`${API}/home/notes/${n.id}/keepsake?token=${token}`)
      .then(() => setNotes(list => list.map(x => (x.id === n.id ? { ...x, kept: true, in_keepsakes: true } : x))))
      .catch(() => setErrorMsg("couldn't add it to From Caleb — try again"));
  };

  const all = notes || [];
  const name = (all.find(n => n.companion)?.companion?.name || 'caleb').toLowerCase();
  const needle = q.trim().toLowerCase();
  const shown = all.filter(n => (!onlyKept || n.kept) && (!onlyWeekly || n.kind === 'weekly') && (!needle || (n.content || '').toLowerCase().includes(needle)));
  const weeklyCount = all.filter(n => n.kind === 'weekly').length;

  // month -> day -> notes (each day's notes in the order he wrote them)
  const months = [];
  shown.forEach(n => {
    const mKey = (n.date || '').slice(0, 7);
    let m = months[months.length - 1];
    if (!m || m.key !== mKey) { m = { key: mKey, label: monthLabel(n.date), days: [] }; months.push(m); }
    let d = m.days[m.days.length - 1];
    if (!d || d.date !== n.date) { d = { date: n.date, notes: [] }; m.days.push(d); }
    d.notes.unshift(n);
  });
  const keptCount = all.filter(n => n.kept).length;

  return (
    <div style={s.page}>
      <link href="https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;1,300;1,400&family=DM+Sans:wght@300;400;500&display=swap" rel="stylesheet" />
      <header style={s.header}>
        <button style={s.back} onClick={() => navigate('/companions')} aria-label="back">←</button>
        <div style={s.title}>notes from {name}</div>
        <button style={s.circle} onClick={() => setEditing(true)} title="how he writes your notes" aria-label="how he writes your notes"><PenIcon /></button>
      </header>
      {editing && <PromptSheet token={token} name={name} onClose={() => setEditing(false)} />}

      <main style={s.main}>
        {errorMsg && <div style={s.error} onClick={() => setErrorMsg('')}>{errorMsg}</div>}

        {all.length > 0 && (
          <div style={s.tools}>
            <div style={s.filters}>
              <button style={s.filterBtn(!onlyKept && !onlyWeekly)} aria-pressed={!onlyKept && !onlyWeekly} onClick={() => { setOnlyKept(false); setOnlyWeekly(false); }}>all {all.length}</button>
              <button style={s.filterBtn(onlyKept)} aria-pressed={onlyKept} onClick={() => { setOnlyKept(true); setOnlyWeekly(false); }}>kept {keptCount}</button>
              {weeklyCount > 0 && (
                <button style={s.filterBtn(onlyWeekly)} aria-pressed={onlyWeekly} onClick={() => { setOnlyWeekly(true); setOnlyKept(false); }}>your weeks {weeklyCount}</button>
              )}
            </div>
            {all.length > 6 && (
              <input style={s.search} value={q} onChange={e => setQ(e.target.value)} placeholder="find a line…" aria-label="search his notes" />
            )}
          </div>
        )}

        {months.map(m => (
          <section key={m.key} style={s.month}>
            <div style={s.monthLabel}>{m.label}</div>
            {m.days.map(d => (
              <div key={d.date} style={s.day}>
                {/* his Sunday letter first, then the day's notes */}
                {[...d.notes.filter(n => n.kind === 'weekly'), ...d.notes.filter(n => n.kind !== 'weekly')].map((n, i, list) => {
                  const weeklyNote = n.kind === 'weekly';
                  const firstDaily = !weeklyNote && list.findIndex(x => x.kind !== 'weekly') === i;
                  const big = weeklyNote || firstDaily;
                  return (
                  <article key={n.id} style={weeklyNote ? s.letter : big ? s.note : s.noteAgain}>
                    <div style={s.meta}>
                      {weeklyNote
                        ? <><span style={s.letterLabel}>about your week</span><span> · {dayLabel(d.date)}</span></>
                        : firstDaily ? <span style={s.dayName}>{dayLabel(d.date)}</span> : <span style={s.also}>he also wrote</span>}
                      {!weeklyNote && <span> · {partOfDay(n.written_at)}</span>}
                    </div>
                    <div style={big ? s.text : s.textSmall}>{n.content}</div>
                    <div style={s.actions}>
                      <button style={s.heartBtn} onClick={() => keep(n)} aria-pressed={n.kept} aria-label={n.kept ? 'unkeep' : 'keep this note'}>
                        <Heart on={n.kept} /> <span>{n.kept ? 'kept' : 'keep'}</span>
                      </button>
                      {n.in_keepsakes
                        ? <span style={s.inKeep}>in From Caleb ♡</span>
                        : n.kept && <button style={s.link} onClick={() => toKeepsakes(n)}>add to From Caleb</button>}
                    </div>
                  </article>
                  );
                })}
              </div>
            ))}
          </section>
        ))}

        {notes && !shown.length && (
          <div style={s.empty}>
            {all.length ? (onlyKept ? 'nothing kept yet — tap the heart on a note you love.' : 'no note has that line.') : `no notes yet — ${name} leaves one on your homepage each day.`}
          </div>
        )}
      </main>
    </div>
  );
}

const s = {
  page: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, overflowY: 'auto', WebkitOverflowScrolling: 'touch', background: '#0e0b0f', color: '#e8ddd0', fontFamily: "'DM Sans', sans-serif", fontWeight: 300 },
  header: { position: 'sticky', top: 0, zIndex: 10, padding: '18px 20px', display: 'flex', alignItems: 'center', gap: '12px', background: 'linear-gradient(180deg, #120f18 0%, #0e0b0f 100%)', borderBottom: '1px solid rgba(201,147,90,0.06)' },
  back: { width: '36px', height: '36px', borderRadius: '50%', border: '1px solid rgba(201,147,90,0.15)', background: 'transparent', color: '#c9935a', fontSize: '16px', cursor: 'pointer', flexShrink: 0 },
  title: { flex: 1, fontFamily: "'Cormorant Garamond', serif", fontSize: '21px', fontStyle: 'italic' },
  main: { maxWidth: '560px', margin: '0 auto', padding: '18px 20px 90px', boxSizing: 'border-box' },
  error: { background: 'rgba(180,60,60,0.12)', border: '1px solid rgba(220,100,100,0.3)', borderRadius: '12px', padding: '10px 14px', fontSize: '12px', color: '#e0a0a0', cursor: 'pointer', marginBottom: '14px' },
  tools: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap', marginBottom: '8px' },
  filters: { display: 'flex', gap: '4px' },
  filterBtn: (on) => ({ padding: '6px 12px', borderRadius: '14px', border: 'none', background: on ? 'rgba(201,147,90,0.14)' : 'transparent', color: on ? '#c9935a' : '#6d5d4e', fontSize: '12.5px', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" }),
  search: { flex: 1, minWidth: '140px', maxWidth: '240px', background: '#120f18', border: '1px solid rgba(201,147,90,0.15)', borderRadius: '10px', padding: '8px 11px', color: '#e8ddd0', fontSize: '13px', fontFamily: "'DM Sans', sans-serif", outline: 'none' },
  month: { marginTop: '22px' },
  monthLabel: { fontSize: '10.5px', letterSpacing: '0.14em', textTransform: 'uppercase', color: '#6d5d4e', margin: '0 2px 12px' },
  day: { marginBottom: '14px' },
  note: { background: '#1e1825', border: '1px solid rgba(201,147,90,0.1)', borderRadius: '18px', padding: '16px 18px 12px' },
  letter: { background: 'linear-gradient(180deg, #221a2a 0%, #1e1825 100%)', border: '1px solid rgba(201,147,90,0.22)', borderRadius: '18px', padding: '16px 18px 12px', marginBottom: '8px' },
  letterLabel: { color: '#d9a3b8', letterSpacing: '0.04em' },
  noteAgain: { margin: '6px 0 0 18px', background: '#17131b', border: '1px solid rgba(201,147,90,0.06)', borderRadius: '16px', padding: '12px 16px 10px' },
  meta: { fontSize: '11.5px', color: '#6d5d4e', marginBottom: '8px' },
  dayName: { color: '#c9a57a' },
  also: { color: '#8a7466', fontStyle: 'italic' },
  text: { fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic', fontSize: '19px', lineHeight: 1.5, color: '#e8ddd0', whiteSpace: 'pre-wrap' },
  textSmall: { fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic', fontSize: '16.5px', lineHeight: 1.5, color: '#cdbfae', whiteSpace: 'pre-wrap' },
  actions: { display: 'flex', alignItems: 'center', gap: '14px', marginTop: '10px', flexWrap: 'wrap' },
  heartBtn: { display: 'inline-flex', alignItems: 'center', gap: '6px', background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: '#8a7466', fontSize: '11.5px', fontFamily: "'DM Sans', sans-serif" },
  link: { background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: '#c9935a', fontSize: '11.5px', fontFamily: "'DM Sans', sans-serif" },
  inKeep: { fontSize: '11.5px', color: '#d9a3b8' },
  circle: { width: '36px', height: '36px', borderRadius: '50%', border: '1px solid rgba(201,147,90,0.15)', background: 'transparent', cursor: 'pointer', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 },
  backdrop: { position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(8,6,10,0.72)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center' },
  sheet: { width: '100%', maxWidth: '560px', maxHeight: '88vh', overflowY: 'auto', boxSizing: 'border-box', background: '#1e1825', border: '1px solid rgba(201,147,90,0.16)', borderBottom: 'none', borderRadius: '22px 22px 0 0', padding: '18px 18px calc(26px + env(safe-area-inset-bottom))', display: 'flex', flexDirection: 'column', gap: '12px' },
  sheetHead: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px' },
  sheetTitle: { fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic', fontSize: '20px' },
  close: { width: '32px', height: '32px', borderRadius: '50%', border: 'none', background: 'rgba(201,147,90,0.08)', color: '#c9935a', fontSize: '18px', cursor: 'pointer', flexShrink: 0 },
  sheetHint: { fontSize: '12px', color: '#8a7466', lineHeight: 1.6 },
  promptArea: { width: '100%', minHeight: '190px', boxSizing: 'border-box', resize: 'vertical', background: '#120f18', border: '1px solid rgba(201,147,90,0.15)', borderRadius: '12px', padding: '12px 14px', color: '#e8ddd0', fontSize: '13.5px', lineHeight: 1.65, fontFamily: "'DM Sans', sans-serif", fontWeight: 300, outline: 'none' },
  toggleRow: { display: 'flex', alignItems: 'flex-start', gap: '10px', fontSize: '13px', color: '#e8ddd0', cursor: 'pointer', lineHeight: 1.5 },
  toggleSub: { display: 'block', fontSize: '11.5px', color: '#8a7466', marginTop: '2px' },
  sample: { background: '#120f18', borderRadius: '14px', padding: '12px 14px', fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic', fontSize: '17px', lineHeight: 1.5, color: '#e8ddd0', whiteSpace: 'pre-wrap' },
  sampleLabel: { fontFamily: "'DM Sans', sans-serif", fontStyle: 'normal', fontSize: '10.5px', letterSpacing: '0.1em', textTransform: 'uppercase', color: '#6d5d4e', marginBottom: '6px' },
  sheetMsg: { fontSize: '12px', color: '#c9a57a' },
  sheetRow: { display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' },
  save: { padding: '9px 20px', borderRadius: '18px', border: 'none', background: 'linear-gradient(135deg, #8a5f35, #c9935a)', color: '#0e0b0f', fontSize: '13px', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif", fontWeight: 400 },
  saveOff: { padding: '9px 20px', borderRadius: '18px', border: 'none', background: 'rgba(201,147,90,0.15)', color: 'rgba(201,147,90,0.45)', fontSize: '13px', fontFamily: "'DM Sans', sans-serif" },
  ghost: { padding: '9px 16px', borderRadius: '18px', border: '1px solid rgba(201,147,90,0.25)', background: 'transparent', color: '#c9935a', fontSize: '13px', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" },
  quietBtn: { padding: '9px 10px', border: 'none', background: 'transparent', color: '#8a7466', fontSize: '12.5px', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" },
  empty: { textAlign: 'center', color: '#6d5d4e', fontSize: '13px', lineHeight: 1.8, padding: '40px 12px' },
};
